import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { dbg, dbgErr } from '../lib/debug';
import { safeText } from '../lib/render';

export default function Dashboard() {
  const [profile, setProfile] = useState(null);
  const [dissertations, setDissertations] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dbg('page', 'Dashboard mounted — fetching /users/me + /dissertations');
    (async () => {
      try {
        const [p, d] = await Promise.all([
          api.get('/api/users/me'),
          api.get('/api/dissertations'),
        ]);
        dbg('page', 'Dashboard profile', p);
        dbg(
          'page',
          `Dashboard dissertations: ${d?.dissertations?.length ?? 0} items`,
          d?.dissertations,
        );
        setProfile(p);
        setDissertations(d.dissertations || []);
      } catch (e) {
        dbgErr('err', 'Dashboard load failed', e);
        setErr(e instanceof ApiError ? e.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return 'Good night';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const credits = profile?.credits ?? 0;
  const isEmpty = credits <= 0;
  const hasEnoughForDissertation = credits >= 31;
  const hasEnoughForAssignment = credits >= 1;

  return (
    <div className="main-container fade-in-up">
      <div className="d-flex align-items-end justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-muted small mb-1">{greeting}</div>
          <h1 className="mb-1" style={{ fontWeight: 800, fontSize: '2rem' }}>
            {profile?.full_name ? profile.full_name.split(' ')[0] : 'Welcome'} 👋
          </h1>
          <p className="text-muted mb-0">Here's what's happening with your account.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {hasEnoughForDissertation && (
            <Link to="/generate" className="btn btn-cta">
              <i className="fas fa-rocket me-2"></i>New dissertation
            </Link>
          )}
          {hasEnoughForAssignment && (
            <Link to="/assignments" className="btn btn-outline-primary">
              <i className="fas fa-file-alt me-2"></i>New assignment
            </Link>
          )}
          <Link to="/pricing" className={isEmpty ? 'btn btn-cta' : 'btn btn-outline-primary'}>
            <i className="fas fa-coins me-2"></i>
            {isEmpty ? 'Buy credits' : 'Top up'}
          </Link>
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {isEmpty && !loading && (
        <div
          className="mb-4 fade-in-up-delay-1"
          style={{
            background:
              'radial-gradient(ellipse at 10% 20%, rgba(245, 158, 11, 0.9) 0%, transparent 55%), radial-gradient(ellipse at 90% 80%, rgba(217, 119, 6, 0.95) 0%, transparent 55%), #7c2d12',
            color: '#fff',
            borderRadius: 'var(--radius-xl)',
            padding: '1.8rem 2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem',
            boxShadow: '0 20px 50px rgba(217, 119, 6, 0.35)',
          }}
        >
          <div style={{ flex: '1 1 320px', minWidth: 260 }}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <i className="fas fa-coins" style={{ fontSize: '1.1rem' }}></i>
              <span
                className="small fw-bold"
                style={{ letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.85 }}
              >
                No credits yet
              </span>
            </div>
            <h4 style={{ fontWeight: 800, margin: 0, lineHeight: 1.25 }}>
              Buy credits to generate your first dissertation
            </h4>
            <p className="mb-0 mt-1" style={{ opacity: 0.85, maxWidth: 540 }}>
              One dissertation = 31 credits. One assignment = 1 credit. Pay per run — no
              subscription.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Link
              to="/pricing"
              className="btn btn-light"
              style={{ padding: '10px 22px', fontWeight: 700 }}
            >
              <i className="fas fa-tags me-2"></i>See plans
            </Link>
            <Link
              to="/samples"
              className="btn btn-outline-light-custom"
              style={{ padding: '10px 22px' }}
            >
              Browse samples
            </Link>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-muted d-flex align-items-center gap-2">
          <span className="spinner-border spinner-border-sm" role="status" />
          Loading your account…
        </div>
      )}

      {profile && (
        <div className="row g-3 mb-4 fade-in-up-delay-1">
          <StatCard
            icon="fa-coins"
            iconColor="#f59e0b"
            label="Credit balance"
            value={credits.toLocaleString()}
            footer={
              isEmpty ? (
                <Link to="/pricing" className="small fw-semibold" style={{ color: '#d97706' }}>
                  Buy credits <i className="fas fa-arrow-right ms-1"></i>
                </Link>
              ) : (
                <span className="text-muted small">
                  ≈ {Math.floor(credits / 31)} dissertations
                </span>
              )
            }
          />
          <StatCard
            icon="fa-book"
            iconColor="#4f46e5"
            label="Dissertations generated"
            value={profile.dissertations_generated}
            footer={<span className="text-muted small">All-time</span>}
          />
          <StatCard
            icon="fa-file-alt"
            iconColor="#0ea5e9"
            label="In progress"
            value={
              dissertations.filter((d) =>
                ['generating', 'in_progress', 'pending'].includes(
                  (d.status || '').toLowerCase(),
                ),
              ).length
            }
            footer={<span className="text-muted small">Live now</span>}
          />
        </div>
      )}

      <div className="card mb-4 fade-in-up-delay-2">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h5 className="mb-0" style={{ fontWeight: 700 }}>
              <i className="fas fa-book me-2" style={{ color: 'var(--primary)' }}></i>
              Your dissertations
            </h5>
            {dissertations.length > 0 && (
              <span className="text-muted small">
                {dissertations.length} total
              </span>
            )}
          </div>
          {dissertations.length === 0 ? (
            <EmptyDissertations isEmpty={isEmpty} />
          ) : (
            <div className="list-group list-group-flush">
              {dissertations.slice(0, 8).map((d) => (
                <Link
                  key={d.id}
                  to={`/dissertation/${d.id}`}
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-3"
                  style={{ border: 'none', borderBottom: '1px solid var(--border)', padding: '1rem 0.25rem' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="fw-semibold text-truncate">
                      {safeText(d.topic, '(untitled)')}
                    </div>
                    <div className="text-muted small mt-1">
                      <i className="fas fa-calendar-alt me-1"></i>
                      {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <span className={`status-badge ${safeText(d.status, 'pending').toLowerCase()}`}>
                      {safeText(d.status, 'pending')}
                    </span>
                    <i className="fas fa-chevron-right text-muted small"></i>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="row g-3 fade-in-up-delay-3">
        <QuickLink to="/worldbank" icon="fa-chart-line" label="World Bank analysis" />
        <QuickLink to="/samples" icon="fa-book-open" label="Browse samples" />
        <QuickLink to="/structure-editor" icon="fa-sliders" label="Structure editor" />
        <QuickLink to="/pricing" icon="fa-coins" label="Buy credits" highlight={isEmpty} />
      </div>
    </div>
  );
}

function StatCard({ icon, iconColor, label, value, footer }) {
  return (
    <div className="col-md-4">
      <div className="card h-100">
        <div className="card-body">
          <div className="d-flex align-items-start gap-3">
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                background: `${iconColor}14`,
                color: iconColor,
                display: 'grid',
                placeItems: 'center',
                fontSize: '1.3rem',
                flexShrink: 0,
              }}
            >
              <i className={`fas ${icon}`}></i>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="text-muted small">{label}</div>
              <div style={{ fontSize: '1.9rem', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                {value}
              </div>
              {footer && <div className="mt-1">{footer}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyDissertations({ isEmpty }) {
  return (
    <div className="text-center py-4">
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 18,
          background: 'var(--primary-soft)',
          color: 'var(--primary)',
          display: 'inline-grid',
          placeItems: 'center',
          fontSize: '1.8rem',
          marginBottom: '1rem',
        }}
      >
        <i className="fas fa-book"></i>
      </div>
      <h6 className="fw-bold">No dissertations yet</h6>
      <p className="text-muted small mb-3" style={{ maxWidth: 360, margin: '0 auto' }}>
        {isEmpty
          ? 'Buy credits from the pricing page, then start your first dissertation.'
          : 'Pick a topic and start your first dissertation — takes about 12 minutes.'}
      </p>
      {isEmpty ? (
        <Link to="/pricing" className="btn btn-cta">
          <i className="fas fa-coins me-2"></i>See pricing
        </Link>
      ) : (
        <Link to="/generate" className="btn btn-cta">
          <i className="fas fa-rocket me-2"></i>Start your first
        </Link>
      )}
    </div>
  );
}

function QuickLink({ to, icon, label, highlight }) {
  return (
    <div className="col-6 col-md-3">
      <Link
        to={to}
        className="card hover-lift h-100 text-decoration-none text-body d-block"
        style={{
          borderColor: highlight ? '#fde68a' : undefined,
          background: highlight
            ? 'linear-gradient(135deg, #fffbeb 0%, var(--surface) 60%)'
            : undefined,
        }}
      >
        <div className="card-body text-center py-4">
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 11,
              background: highlight
                ? 'linear-gradient(135deg, var(--accent), #d97706)'
                : 'var(--primary-soft)',
              color: highlight ? '#fff' : 'var(--primary)',
              display: 'inline-grid',
              placeItems: 'center',
              fontSize: '1.15rem',
              marginBottom: '0.7rem',
            }}
          >
            <i className={`fas ${icon}`}></i>
          </div>
          <div className="fw-semibold small">{label}</div>
        </div>
      </Link>
    </div>
  );
}
